import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, Typography, CircularProgress, Paper, TextField, Alert } from '@mui/material';
import { supabase } from '../../Supabase/supabaseRealtimeClient';
import { VideoAtendimento } from '../../types/telemedicina';
import VideoRoom from './VideoRoom';
import { useNavigate } from 'react-router-dom';

const TelemedicinaClient: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [activeCall, setActiveCall] = useState<VideoAtendimento | null>(null);
  const [queixa, setQueixa] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const subscriptionRef = useRef<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        setLoading(true);
        
        // Tenta recuperar a sessão se não existir
        let { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          const accessToken = localStorage.getItem('accessToken');
          const refreshToken = localStorage.getItem('refreshToken');
          
          if (accessToken && refreshToken) {
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (!error && data.user) {
              user = data.user;
            }
          }
        }
        
        if (!user) {
          if (mounted) setError("Usuário não autenticado.");
          return;
        }
        
        if (mounted) setUserId(user.id);

        // Get Cliente ID
        const { data: clienteData, error: clienteError } = await supabase
          .from('cliente')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (clienteError) {
          console.error('Error fetching cliente:', clienteError);
          if (mounted) setError("Erro ao buscar dados do cliente.");
          return;
        }

        if (!clienteData) {
          console.error('Cliente not found for user_id:', user.id);
          if (mounted) setError("Perfil de cliente não encontrado. Entre em contato com o suporte.");
          return;
        }

        if (mounted) setClienteId(clienteData.id);

        // Get Active Call
        const { data: callData } = await supabase
          .from('video_atendimentos')
          .select('*')
          .eq('cliente_id', clienteData.id)
          .in('status', ['aguardando_triagem', 'em_triagem', 'aguardando_medico', 'em_consulta'])
          .maybeSingle();

        if (callData && mounted) {
          setActiveCall(callData);
        }

      } catch (err) {
        console.error(err);
        if (mounted) setError("Ocorreu um erro inesperado.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, []);

  // Subscription Effect - Depende de clienteId
  useEffect(() => {
    if (!clienteId) return;

    const channelName = `video_atendimentos_cliente_${clienteId}`;
    console.log(`[TelemedicinaClient] Subscribing to channel: ${channelName}`);
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'video_atendimentos',
          filter: `cliente_id=eq.${clienteId}`
        },
        async (payload) => {
          console.log('[TelemedicinaClient] Realtime event received:', payload);
          
          if (payload.eventType === 'DELETE') {
            console.log('[TelemedicinaClient] Chamado deletado. Resetando estado.');
            setActiveCall(null);
            navigate('/telemedicina/cliente');
          } else if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const newCall = payload.new as VideoAtendimento;
            console.log('[TelemedicinaClient] Status atualizado:', newCall.status);
            
            // Se cancelado ou finalizado, resetar
            if (['finalizado', 'cancelado'].includes(newCall.status)) {
              console.log('[TelemedicinaClient] Chamado encerrado/cancelado. Resetando estado.');
              setActiveCall(null);
              navigate('/telemedicina/cliente');
            } else {
              // Atualizar estado para refletir mudança (aguardando -> em_triagem -> aguardando_medico -> em_consulta)
              console.log('[TelemedicinaClient] Atualizando activeCall para:', newCall.status);
              setActiveCall({ ...newCall }); 
            }
          }
        }
      )
      .subscribe((status) => {
        console.log(`[TelemedicinaClient] Subscription status for ${channelName}: ${status}`);
        if (status === 'SUBSCRIBED') {
           console.log('[TelemedicinaClient] Realtime Connected!');
        }
      });
    
    subscriptionRef.current = channel;

    return () => {
      console.log(`[TelemedicinaClient] Unsubscribing from ${channelName}`);
      supabase.removeChannel(channel);
    };
  }, [clienteId]);


  const requestCall = async () => {
    if (!clienteId) {
      alert("Erro: Identificação do cliente não encontrada. Tente recarregar a página.");
      return;
    }
    
    try {
      setLoading(true);
      
      // Double check if there isn't already an active call
      const { data: existingCall } = await supabase
          .from('video_atendimentos')
          .select('*')
          .eq('cliente_id', clienteId)
          .in('status', ['aguardando_triagem', 'em_triagem', 'aguardando_medico', 'em_consulta'])
          .maybeSingle();
      
      if (existingCall) {
        setActiveCall(existingCall);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('video_atendimentos')
        .insert({
          cliente_id: clienteId,
          status: 'aguardando_triagem',
          queixa_inicial: queixa,
          room_id: `room-${Date.now()}-${clienteId}` // Simple room ID generation
        })
        .select()
        .single();

      if (error) throw error;
      setActiveCall(data);
    } catch (error: any) {
      console.error('Error requesting call:', error);
      alert(`Erro ao solicitar atendimento: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLeave = () => {
    if (!activeCall) return;
    
    // Se o usuário CANCELAR a solicitação (ainda não foi atendido)
    if (activeCall.status === 'aguardando_triagem' || activeCall.status === 'aguardando_medico') {
       supabase.from('video_atendimentos')
       .update({ status: 'cancelado' })
       .eq('id', activeCall.id)
       .then(({ error }) => {
           if (error) console.error('Erro ao cancelar:', error);
           // A atualização virá via realtime, mas podemos forçar localmente para UX imediata
           setActiveCall(null);
       });
    } else {
       // Se estiver em chamada, apenas limpa o estado e volta para a tela inicial
       setActiveCall(null);
       navigate('/telemedicina/cliente'); // Ou qualquer outra rota apropriada
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Alert severity="error">{error}</Alert>
    </Box>
  );

  const refreshStatus = async () => {
    if (!activeCall) return;
    setLoading(true);
    const { data } = await supabase
      .from('video_atendimentos')
      .select('*')
      .eq('id', activeCall.id)
      .single();
    
    if (data) {
      setActiveCall(data);
    }
    setLoading(false);
  };

  const handleEndCall = async () => {
    if (!activeCall) return;
    try {
        setLoading(true);
        const { error } = await supabase
            .from('video_atendimentos')
            .update({ status: 'finalizado', data_fechamento: new Date().toISOString() })
            .eq('id', activeCall.id);

        if (error) throw error;
        setActiveCall(null);
        navigate('/telemedicina/cliente');
    } catch (err: any) {
        console.error('Erro ao encerrar atendimento:', err);
        alert(`Erro ao encerrar atendimento: ${err.message}`);
    } finally {
        setLoading(false);
    }
  };

  if (activeCall) {
    // Se o status for 'aguardando_triagem' ou 'aguardando_medico', mostramos a tela de espera.
    // Se o status for 'em_triagem' ou 'em_consulta', mostramos a VideoRoom.
    const inCall = ['em_triagem', 'em_consulta'].includes(activeCall.status);
    
    // Debug
    console.log('[TelemedicinaClient] Render check - Status:', activeCall.status, 'InCall:', inCall);
    
    if (inCall && userId) {
      return (
        <Box sx={{ height: '90vh' }}>
          <VideoRoom 
            roomId={`room-${activeCall.id}`}
            userId={userId} 
            onLeave={handleLeave}
            onEndCall={handleEndCall}
          />
        </Box>
      );
    }
    
    // Se NÃO estiver em chamada (ou userId nulo), cai aqui: tela de espera
    // Isso cobre 'aguardando_triagem' e 'aguardando_medico'
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Aguardando Atendimento
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Status atual: <strong>{activeCall.status.replace('_', ' ').toUpperCase()}</strong>
          </Typography>
          <CircularProgress size={50} sx={{ mb: 3 }} />
          <Typography variant="body2" color="text.secondary">
            {activeCall.status === 'aguardando_medico' 
                ? 'Você foi encaminhado para o médico. Aguarde um momento.' 
                : 'Por favor, aguarde. Um profissional irá atendê-lo em breve.'}
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            {/* Botão de atualizar removido conforme solicitação, atualização é automática via realtime */}
            <Button variant="outlined" color="error" onClick={handleLeave}>
              Cancelar Solicitação
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Telemedicina
        </Typography>
        <Typography variant="body1" paragraph>
          Clique no botão abaixo para iniciar o atendimento. Se desejar, descreva seus sintomas.
        </Typography>
        <TextField
          label="Queixa Principal (Opcional)"
          multiline
          rows={4}
          fullWidth
          value={queixa}
          onChange={(e) => setQueixa(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Button 
          variant="contained" 
          color="primary" 
          fullWidth 
          onClick={requestCall}
          sx={{ mb: 2 }}
        >
          Solicitar Vídeo Chamada
        </Button>
        <Button 
            variant="outlined" 
            fullWidth
            onClick={() => navigate(-1)}
        >
            Voltar
        </Button>
      </Paper>
    </Box>
  );
};

export default TelemedicinaClient;
